import { z } from 'zod'

import { listQueryZodFields } from './system'
import { dbEntitySchema } from './system'
import { MicrositeBlockType } from '../constants/micrositePage'

type MicrositeBlockInput = {
  type: MicrositeBlockType
  props?: Record<string, unknown>
  children?: MicrositeBlockInput[]
}

type MicrositeBlock = MicrositeBlockInput & {
  _id: string
}

type MicrositePageMeta = {
  name?: string
  property?: string
  httpEquiv?: string
  charset?: string
  content?: string
}

const propsSchema = z
  .preprocess((value) => {
    if (value === undefined) return undefined
    if (value instanceof Map) {
      return Object.fromEntries(value as Map<string, unknown>)
    }
    return value
  }, z.record(z.unknown()))
  .optional() as unknown as z.ZodType<Record<string, unknown> | undefined>

const blockPropsShape = {
  type: z.nativeEnum(MicrositeBlockType),
  props: propsSchema,
}

export const micrositeBlockCreateArgsSchema: z.ZodType<MicrositeBlockInput> =
  z.lazy(() =>
    z.object({
      ...blockPropsShape,
      children: z.array(micrositeBlockCreateArgsSchema).optional(),
    })
  )

export const micrositeBlockSchema: z.ZodType<MicrositeBlock> = z.lazy(() =>
  z
    .object({
      _id: z.string().min(1),
      ...blockPropsShape,
      children: z.array(micrositeBlockSchema).optional(),
    })
    .transform((value) => ({
      ...value,
      children: value.children ?? [],
    }))
)

export const micrositePageMetaSchema: z.ZodType<MicrositePageMeta> = z
  .object({
    name: z.string().min(1).optional(),
    property: z.string().min(1).optional(),
    httpEquiv: z.string().min(1).optional(),
    charset: z.string().min(1).optional(),
    content: z.string().max(1000).optional(),
  })
  .refine(
    (value) =>
      Boolean(value.name) ||
      Boolean(value.property) ||
      Boolean(value.httpEquiv) ||
      Boolean(value.charset),
    {
      message:
        'meta entry requires at least one of name, property, httpEquiv, or charset',
    }
  )

export const micrositePageSchema = dbEntitySchema.extend({
  microsite: z.string(),
  organization: z.string(),
  venue: z.string(),
  title: z.string().min(1).max(200),
  slug: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9-]+$/),
  blocks: z.array(micrositeBlockSchema).default([]),
  meta: z.array(micrositePageMetaSchema).default([]),
  published: z.boolean().default(false),
})

export const micrositePageListArgsSchema = z.object({
  ...listQueryZodFields,
  organizationId: z.string().optional(),
  venueId: z.string().optional(),
  micrositeId: z.string().optional(),
  published: z.boolean().optional(),
})

export const micrositePageCreateArgsSchema = micrositePageSchema
  .omit({
    _id: true,
    id: true,
    deleted: true,
    organization: true,
    venue: true,
    created: true,
    modified: true,
    published: true,
    microsite: true,
    blocks: true,
  })
  .extend({
    micrositeId: z.string(),
    published: z.boolean().optional(),
    blocks: z.array(micrositeBlockCreateArgsSchema).optional(),
    meta: z.array(micrositePageMetaSchema).optional(),
  })

export const micrositePageUpdateByIdArgsSchema = micrositePageSchema
  .omit({
    _id: true,
    id: true,
    organization: true,
    venue: true,
    microsite: true,
    created: true,
    modified: true,
    deleted: true,
    blocks: true,
  })
  .partial()
  .extend({
    blocks: z.array(micrositeBlockCreateArgsSchema).optional(),
  })

// Responses
export const micrositePageListResponseSchema = z.object({
  data: z.array(micrositePageSchema),
})
